import { useRef, useState, useEffect } from 'react';
import { STORAGE_KEY, BOARD_WIDTH, BOARD_HEIGHT, getPointerCoords } from '../utils/whiteboardUtils';

export const useWhiteboardPan = (id, svgRef, tool, { setIsDragOver, setShapes, setPdfModal }) => {
  const panRef = useRef({ x: 0, y: 0, scale: 0.5 });
  const isPanning = useRef(false);
  const lastPan = useRef({ x: 0, y: 0 });

  const [pan, setPan] = useState(() => {
    const saved = localStorage.getItem(`${STORAGE_KEY}_panzoom_${id}`);
    if (saved) {
      return JSON.parse(saved);
    }
    const centerX = (window.innerWidth - BOARD_WIDTH * 1.0) / 2;
    const centerY = (window.innerHeight - BOARD_HEIGHT * 1.0) / 2;
    return { x: centerX, y: centerY, scale: 1.0 };
  });

  // eslint-disable-next-line no-unused-vars
  const [isDragging, setIsDragging] = useState(false);
  const [isDraggingBoard, setIsDraggingBoard] = useState(false);
  const [spacePressed, setSpacePressed] = useState(false);

  useEffect(() => {
    panRef.current = pan;
  }, [pan]);

  useEffect(() => {
    try {
      localStorage.setItem(`${STORAGE_KEY}_panzoom_${id}`, JSON.stringify(pan));
    } catch (error) {
      // Игнорируем ошибки localStorage
    }
  }, [pan, id]);

  const updatePan = (newPan) => {
    if (typeof newPan === 'function') {
      setPan(prevPan => {
        const updatedPan = newPan(prevPan);
        const maxX = 0;
        const minX = -(BOARD_WIDTH * updatedPan.scale - window.innerWidth);
        const maxY = 0;
        const minY = -(BOARD_HEIGHT * updatedPan.scale - window.innerHeight);

        const limitedPan = {
          ...updatedPan,
          x: Math.max(Math.min(updatedPan.x, maxX), minX),
          y: Math.max(Math.min(updatedPan.y, maxY), minY)
        };

        panRef.current = limitedPan;
        return limitedPan;
      });
    } else {
      const maxX = 0;
      const minX = -(BOARD_WIDTH * newPan.scale - window.innerWidth);
      const maxY = 0;
      const minY = -(BOARD_HEIGHT * newPan.scale - window.innerHeight);

      const limitedPan = {
        ...newPan,
        x: Math.max(Math.min(newPan.x, maxX), minX),
        y: Math.max(Math.min(newPan.y, maxY), minY)
      };

      panRef.current = limitedPan;
      setPan(limitedPan);
    }
  };

  // Wheel zoom + document-level drag/drop
  useEffect(() => {
    let rafId = null;
    let isPanningLocal = false;

    const handleWheel = e => {
      e.preventDefault();

      if (!e.ctrlKey && !e.metaKey) {
        if (Math.abs(e.deltaX) > 0 || Math.abs(e.deltaY) > 0) {
          isPanningLocal = true;
        }
        if (isPanningLocal) {
          updatePan(p => {
            const newX = p.x - e.deltaX;
            const newY = p.y - e.deltaY;
            const maxX = 0;
            const minX = -(BOARD_WIDTH * p.scale - window.innerWidth);
            const maxY = 0;
            const minY = -(BOARD_HEIGHT * p.scale - window.innerHeight);
            return {
              ...p,
              x: Math.max(Math.min(newX, maxX), minX),
              y: Math.max(Math.min(newY, maxY), minY)
            };
          });
        }
        return;
      }

      if (e.ctrlKey || e.metaKey) {
        const scaleBy = 1.08;
        let newScale = panRef.current.scale * (e.deltaY < 0 ? scaleBy : 1 / scaleBy);
        newScale = Math.max(0.1, Math.min(3.0, newScale));

        const mouseX = e.clientX;
        const mouseY = e.clientY;
        const worldX = (mouseX - panRef.current.x) / panRef.current.scale;
        const worldY = (mouseY - panRef.current.y) / panRef.current.scale;
        const newX = mouseX - worldX * newScale;
        const newY = mouseY - worldY * newScale;

        if (rafId) cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          updatePan(p => {
            const updatedPan = { ...p, scale: newScale, x: newX, y: newY };
            if (Math.abs(newScale - 1.0) < 0.01) {
              const centerX = (window.innerWidth - BOARD_WIDTH * 1.0) / 2;
              const centerY = (window.innerHeight - BOARD_HEIGHT * 1.0) / 2;
              return { x: centerX, y: centerY, scale: 1.0 };
            }
            return updatedPan;
          });
        });
      }
    };

    const handleWheelEnd = () => { isPanningLocal = false; };

    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('wheelend', handleWheelEnd);
    window.addEventListener('mouseup', handleWheelEnd);

    const handleDragEnter = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
    };

    const handleDragLeave = (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!e.relatedTarget || !document.contains(e.relatedTarget)) {
        setIsDragOver(false);
      }
    };

    const handleDragOver = (e) => {
      e.preventDefault();
      e.stopPropagation();
    };

    const handleDrop = (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(false);

      const files = Array.from(e.dataTransfer.files);
      if (files.length > 0) {
        const file = files[0];
        const url = URL.createObjectURL(file);
        const { x, y } = getPointerCoords(e, panRef.current, svgRef);

        if (file.type.startsWith('image/')) {
          const newId = Date.now();
          setShapes(prev => [...prev, { id: newId, type: 'image', x, y, width: 200, height: 150, src: url, filename: file.name }]);
        } else if (file.type.startsWith('video/')) {
          const newId = Date.now();
          setShapes(prev => [...prev, { id: newId, type: 'video', x, y, width: 300, height: 200, src: url, filename: file.name }]);
        } else if (file.type.startsWith('audio/')) {
          const newId = Date.now();
          setShapes(prev => [...prev, { id: newId, type: 'audio', x, y, width: 300, height: 80, src: url, filename: file.name }]);
        } else if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
          setPdfModal({ pdfUrl: url, filename: file.name, x, y });
        }
      }
    };

    document.addEventListener('dragenter', handleDragEnter);
    document.addEventListener('dragleave', handleDragLeave);
    document.addEventListener('dragover', handleDragOver);
    document.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('wheel', handleWheel);
      window.removeEventListener('wheelend', handleWheelEnd);
      window.removeEventListener('mouseup', handleWheelEnd);
      document.removeEventListener('dragenter', handleDragEnter);
      document.removeEventListener('dragleave', handleDragLeave);
      document.removeEventListener('dragover', handleDragOver);
      document.removeEventListener('drop', handleDrop);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Touch pinch zoom
  useEffect(() => {
    let lastDist = null;
    let lastMid = null;

    const handleTouchMove = e => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const [t1, t2] = e.touches;
        const dx = t2.clientX - t1.clientX;
        const dy = t2.clientY - t1.clientY;
        const dist = Math.sqrt(dx * dx + dy * dy);
        const mid = { x: (t1.clientX + t2.clientX) / 2, y: (t1.clientY + t2.clientY) / 2 };
        if (lastDist && lastMid) {
          const scaleBy = dist / lastDist;
          const smoothScaleBy = 1 + (scaleBy - 1) * 0.6;
          let newScale = panRef.current.scale * smoothScaleBy;
          newScale = Math.max(0.1, Math.min(3.0, newScale));
          const rect = svgRef.current.getBoundingClientRect();
          const mx = mid.x - rect.left;
          const my = mid.y - rect.top;
          const wx = (mx - panRef.current.x) / panRef.current.scale;
          const wy = (my - panRef.current.y) / panRef.current.scale;
          const nx = mx - wx * newScale;
          const ny = my - wy * newScale;
          updatePan(p => ({
            ...p,
            scale: newScale,
            x: Math.max(Math.min(nx, 0), -(BOARD_WIDTH * newScale - window.innerWidth)),
            y: Math.max(Math.min(ny, 0), -(BOARD_HEIGHT * newScale - window.innerHeight)),
          }));
        }
        lastDist = dist;
        lastMid = mid;
      }
    };

    const handleTouchEnd = () => {
      lastDist = null;
      lastMid = null;
    };

    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Touch 2-finger pan
  useEffect(() => {
    let lastTouch = null;

    function handleTouchStart(e) {
      if (e.touches.length === 2) {
        setIsDraggingBoard(true);
        isPanning.current = true;
        lastTouch = [
          { x: e.touches[0].clientX, y: e.touches[0].clientY },
          { x: e.touches[1].clientX, y: e.touches[1].clientY }
        ];
      }
    }

    function handleTouchMove(e) {
      if (e.touches.length === 2 && isPanning.current && lastTouch) {
        const curr = [
          { x: e.touches[0].clientX, y: e.touches[0].clientY },
          { x: e.touches[1].clientX, y: e.touches[1].clientY }
        ];
        const dx = ((curr[0].x + curr[1].x) - (lastTouch[0].x + lastTouch[1].x)) / 2;
        const dy = ((curr[0].y + curr[1].y) - (lastTouch[0].y + lastTouch[1].y)) / 2;
        updatePan(p => {
          let nx = p.x + dx;
          let ny = p.y + dy;
          const maxX = 0;
          const minX = -(BOARD_WIDTH * p.scale - window.innerWidth);
          const maxY = 0;
          const minY = -(BOARD_HEIGHT * p.scale - window.innerHeight);
          nx = Math.max(Math.min(nx, maxX), minX);
          ny = Math.max(Math.min(ny, maxY), minY);
          return { ...p, x: nx, y: ny };
        });
        lastTouch = curr;
      }
    }

    function handleTouchEnd(e) {
      if (e.touches.length < 2) {
        setIsDraggingBoard(false);
        isPanning.current = false;
        lastTouch = null;
      }
    }

    window.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd);
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Whiteboard-container wheel (fine-grained zoom)
  useEffect(() => {
    const handleWheel = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (e.ctrlKey || e.metaKey) {
        const delta = e.deltaY > 0 ? 0.985 : 1.015;
        const newScale = Math.max(0.1, Math.min(3, panRef.current.scale * delta));
        const rect = svgRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;
        const scaleRatio = newScale / panRef.current.scale;
        const newPanX = mouseX - (mouseX - panRef.current.x) * scaleRatio;
        const newPanY = mouseY - (mouseY - panRef.current.y) * scaleRatio;
        updatePan({ x: newPanX, y: newPanY, scale: newScale });
      } else {
        const deltaX = e.deltaX || 0;
        const deltaY = e.deltaY || 0;
        updatePan({
          x: panRef.current.x - deltaX,
          y: panRef.current.y - deltaY,
          scale: panRef.current.scale
        });
      }
    };

    const whiteboardContainer = document.querySelector('.whiteboard-container');
    if (whiteboardContainer) {
      whiteboardContainer.addEventListener('wheel', handleWheel, { passive: false });
    }
    return () => {
      if (whiteboardContainer) {
        whiteboardContainer.removeEventListener('wheel', handleWheel, { passive: false });
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const dragPanStart = e => {
    if (tool === 'text') return;
    if (e.button !== undefined && e.button !== 0) return;
    if (e.touches && e.touches.length > 1) return;
    if (e.target.closest('form')) return;
    setIsDraggingBoard(true);
    lastPan.current = {
      x: e.touches ? e.touches[0].clientX : e.clientX,
      y: e.touches ? e.touches[0].clientY : e.clientY
    };
  };

  const dragPanMove = e => {
    if (!isDraggingBoard) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    updatePan(p => {
      let nx = p.x + (x - lastPan.current.x);
      let ny = p.y + (y - lastPan.current.y);
      const maxX = 0;
      const minX = -(BOARD_WIDTH * p.scale - window.innerWidth);
      const maxY = 0;
      const minY = -(BOARD_HEIGHT * p.scale - window.innerHeight);
      nx = Math.max(Math.min(nx, maxX), minX);
      ny = Math.max(Math.min(ny, maxY), minY);
      return { ...p, x: nx, y: ny };
    });
    lastPan.current = { x, y };
  };

  const dragPanEnd = () => {
    setIsDraggingBoard(false);
    isPanning.current = false;
  };

  function handleZoomIn() {
    updatePan(p => ({ ...p, scale: Math.min(3, p.scale * 1.1) }));
  }

  function handleZoomOut() {
    updatePan(p => {
      let newScale = Math.max(0.1, p.scale / 1.1);
      if (Math.abs(newScale - 1.0) < 0.01) {
        const centerX = (window.innerWidth - BOARD_WIDTH * 1.0) / 2;
        const centerY = (window.innerHeight - BOARD_HEIGHT * 1.0) / 2;
        return { x: centerX, y: centerY, scale: 1.0 };
      }
      return { ...p, scale: newScale };
    });
  }

  function handleZoomToFit() {
    updatePan({
      x: (window.innerWidth - BOARD_WIDTH * 1.0) / 2,
      y: (window.innerHeight - BOARD_HEIGHT * 1.0) / 2,
      scale: 1.0
    });
  }

  const zoomPercent = Math.round(pan.scale * 100);

  return {
    pan, panRef, isPanning, lastPan,
    updatePan, spacePressed, setSpacePressed,
    isDragging, setIsDragging, isDraggingBoard, setIsDraggingBoard,
    dragPanStart, dragPanMove, dragPanEnd,
    handleZoomIn, handleZoomOut, handleZoomToFit, zoomPercent,
  };
};
